<template>
  <b-modal
    id="stripe-terminal-payment-modal"
    :title="trans('stripe_terminal')"
    no-close-on-backdrop
    no-enforce-focus
    no-close-on-esc
    hide-header-close
    ok-only
    :ok-title="trans('cancel')"
    ok-variant="outline-secondary"
    :hide-footer="!!terminal_reader"
    :ok-disabled="busy"
    @shown="onShown"
    @hidden="onHidden"
  >
    <b-row>
      <b-col>
        <b-form-group :label="trans('amount')">
          <h3><dollar :value="displayAmount"></dollar></h3>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group>
          <label>
            {{ trans('reader') }}
          </label>
          <b-form-select size="sm" v-model="terminal_reader_id" :options="terminalReaderOptions" :disabled="busy || !terminal_readers || !!terminal_reader" @change="onSelectTerminal">
          </b-form-select>
        </b-form-group>
      </b-col>
    </b-row>

    <p v-if="noTerminalReaders" v-html="trans('no_stripe_terminal')" class="text-center lead py-4"></p>

    <div v-if="!allReadersAreOffline && terminalReaderOptions.length > 0" class="my-2 py-2 text-center">
      <b-button variant="primary" size="lg" @click="onClickProcess" :disabled="busy">
        {{ trans('send_to_terminal') }}
      </b-button>
      <b-button v-if="!!terminal_reader" variant="link" size="lg" class="text-danger" @click="onClickCancel">
        {{ trans('cancel') }}
      </b-button>
    </div>

    <div v-if="allReadersAreOffline" class="text-center">
      <p class="lead py-4">
        {{ trans('all_terminals_are_offline') }}
      </p>
      <b-button variant="outline-primary" size="lg" @click="fetchTerminalReaders" :disabled="busy">
        <i v-if="!busy" class="fa fa-fw fa-redo"></i>
        <i v-if="busy" class="fa fa-fw fa-circle-notch fa-spin"></i>
      </b-button>
    </div>

    <p v-if="busy && !failed" class="text-center lead py-4">
      <i class="fa fa-fw fa-circle-notch fa-spin"></i>
      {{ status }}
    </p>
    <p v-if="failed" class="text-center text-danger lead py-4">
      <i class="fa fa-fw fa-exclamation-triangle"></i>
      {{ status }}
    </p>
    <p v-if="error" class="text-center text-danger py-4">
      {{ error }}
    </p>
  </b-modal>
</template>

<script>
  import Dollar from './../billing/Dollar'

  function getTerminalReaderIdFromLocalStorage(organization) {
    let key = 'organization_' + organization.id + '_terminal_reader_id'
    return localStorage.getItem(key) || null
  }
  function setTerminalReaderIdToLocalStorage(organization, terminal_reader_id) {
    let key = 'organization_' + organization.id + '_terminal_reader_id'
    localStorage.setItem(key, terminal_reader_id)
  }

  export default {
    props: {
      organization: Object,
      invoice: Object,
      recipient: Object, // When set, someone else than the invoice's recipient will pay
      amount: Number|String,
      includeCcFee: Boolean,
      amountWithCcFee: Number|String,
      stripeId: String, // Set in the Microsite Checkout, undefined on the admin side
      clientSecret: String, // Set in the Microsite Checkout, undefined on the admin side
    },
    components: {
      Dollar,
    },
    data() {
      return {
        trans: trans,
        busy: false,
        error: null,
        numeral: numeral,
        currency: _.currency,
        stripe_id: null,
        terminal_readers: null,
        terminal_reader_id: null, // Chosen
        terminal_reader: null, // Stripe reader contains the status - poll it!
        progress_timeout_id: null,
      }
    },
    computed: {
      displayAmount() {
        return this.includeCcFee ? this.amountWithCcFee : this.amount
      },
      terminalReaderOptions() {
        return _.map(this.terminal_readers || [], r => {
          return {
            value: r.id,
            text: `${r.label} (${trans(r.status)})`,
            disabled: r.status == 'offline'
          }
        })
      },
      action() {
        return !!this.terminal_reader && !!this.terminal_reader.action ?
         this.terminal_reader.action :
         null
      },
      noTerminalReaders() {
        return !this.busy && !!this.terminal_readers && this.terminalReaderOptions.length == 0
      },
      firstOnlineTerminalReader() {
        if (!this.terminal_readers) return null
        return _.reduce(this.terminal_readers, (found, r) => {
          if (!found && r.status == 'online') found = r.id
          return found
        }, null)
      },
      allReadersAreOffline() {
        if (this.busy || this.noTerminalReaders) return false
        return !this.firstOnlineTerminalReader
      },
      failed() {
        return !!this.action && this.action.status == 'failed'
      },
      status() {
        if (!!this.action) {
          if (this.action.status == 'in_progress') {
            return trans('give_terminal_to_client')
          }
          return trans(this.action.status)
        }
        return this.busy ? trans('please_wait') : null
      },
    },
    methods: {
      onSelectTerminal() {
        if (this.terminal_reader_id) {
          setTerminalReaderIdToLocalStorage(this.organization, this.terminal_reader_id)
        }
      },
      onClickProcess() {
        this.busy = true
        this.error = false
        return axios.post(`/organization/${this.organization.id}/invoice/${this.invoice.id}/process_admin_stripe_terminal_payment`, {
          payment_intent_id: this.stripe_id,
          terminal_reader_id: this.terminal_reader_id,
        })
        .then(response => {
          this.terminal_reader = response.data
          this.progress_timeout_id = setTimeout(() => {
            this.progress_timeout_id = null
            this.pollTerminalReader()
          }, 4000)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      pollTerminalReader() {
        if (!this.terminal_reader) {
          this.busy = false
          return
        }
        this.busy = true
        this.error = false
        let url = `/organization/${this.organization.id}/stripe/terminal_reader/${this.terminal_reader.id}`
        return axios.get(url)
        .then(response => {
          this.terminal_reader = response.data
          if (this.terminal_reader.action) {
            if (this.terminal_reader.action.status == 'succeeded') {
              this.onSuccess()
            }
            else if (this.terminal_reader.action.status == 'in_progress') {
              this.progress_timeout_id = setTimeout(() => {
                this.progress_timeout_id = null
                this.pollTerminalReader()
              }, 2000)
            }
            else if (this.terminal_reader.action.status == 'failed') {
              this.error = this.terminal_reader.action.failure_message
              this.terminal_reader = null
              this.busy = false
            }
          }
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onSuccess() {
        setTimeout(() => {
          this.$emit('paid', this.stripe_id)
          this.$bvModal.hide('stripe-terminal-payment-modal')
        }, 500)
      },
      onClickCancel() {
        if (this.progress_timeout_id) {
          clearTimeout(this.progress_timeout_id)
          this.progress_timeout_id = null
        }
        this.busy = true
        this.error = false
        let url = `/organization/${this.organization.id}/stripe/terminal_reader/${this.terminal_reader.id}/cancel_action`
        return axios.delete(url)
        .then(response => {
          this.busy = false
          this.terminal_reader = null
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onShown() {
        this.preparePayment()
      },
      onHidden() {
        this.busy = false
        this.error = null
        this.stripe_id = null
        this.terminal_readers = null
        this.terminal_reader_id = null
        this.terminal_reader = null
        if (this.progress_timeout_id) clearTimeout(this.progress_timeout_id)
        this.progress_timeout_id = null
      },
      preparePayment() {
        if (this.amount  == 0) return
        if (!!this.stripeId) {
          this.stripe_id = this.stripeId
          this.fetchTerminalReaders()
          return
        }
        this.busy = true
        this.error = false
        let url = `/organization/${this.organization.id}/invoice/${this.invoice.id}/prepare_admin_payment`
        if (!!this.recipient) url += `/${this.recipient.id}`
        return axios.post(url, {
          amount: this.amount,
          include_cc_fee: this.includeCcFee,
          card_present: true,
        })
        .then(response => {
          this.busy = false
          this.stripe_id = response.data.stripe_info.stripe_id
          this.$emit('client-secret', response.data.stripe_info.client_secret)
          this.fetchTerminalReaders()
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      fetchTerminalReaders() {
        this.error = null
        this.busy = true
        let url = `/organization/${this.organization.id}/stripe/terminal_reader`
        axios.get(url)
        .then(response => {
          this.busy = false
          this.terminal_readers = response.data
          this.$nextTick(() => {
            if (!this.terminal_reader_id) {
              this.terminal_reader_id = getTerminalReaderIdFromLocalStorage(this.organization) || this.firstOnlineTerminalReader
            }
          })
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
          this.terminal_readers = []
        })
      },
    },
    mounted() {
      window.stripeTerminalPaymentModal = this
    },
  }
</script>